/* eslint-disable @angular-eslint/prefer-inject */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthenticationService } from '../services/authentication.service';
import { JWTTokenService } from '../services/jwttoken.service';
import { LocalStorageService } from '../services/storage/local-storage.service';
import { StorageKeys } from '../services/storage/StorageKeys';

interface TokenState {
  token: string;
  expiry: number;
}

@Injectable()
export class DynamicAuthInterceptor implements HttpInterceptor {
  private currentAuthMethod: string | undefined;
  private jwtState: TokenState | null = null;
  private msalState: TokenState | null = null;
  private readonly TOKEN_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
  private readonly MSAL_TOKEN_DURATION_MS = 60 * 60 * 1000; // 1 hour

  constructor(
    private authService: AuthenticationService,
    private storageService: LocalStorageService,
    private jwtTokenService: JWTTokenService,
    @Optional() private msalService: MsalService,
  ) {
    this.authService.currentAuth$.subscribe((result: any) => {
      if (result) {
        this.currentAuthMethod = this.authService.getCurrentAuthMethod();
        if (this.currentAuthMethod === 'jwt') {
          this.initializeJwtToken();
        }
      }
    });
  }

  private initializeJwtToken(): void {
    const token = this.storageService.getSessionStorage(StorageKeys.Token);
    if (token) {
      const data = this.jwtTokenService.getTokenData(token);
      const expiry = data.exp;
      if (expiry) {
        this.jwtState = { token, expiry };
      }
    }
  }

  private isTokenExpired(expiry: number | undefined): boolean {
    return !expiry || Date.now() >= expiry - this.TOKEN_BUFFER_MS;
  }

  private refreshMsalToken(): Observable<string> {
    return this.authService.getAccessTokenSilently().pipe(
      tap((token: string) => {
        this.msalState = {
          token,
          expiry: Date.now() + this.MSAL_TOKEN_DURATION_MS,
        };
      }),
    );
  }

  private isMsalAuthUrl(url: string): boolean {
    return (
      url.includes('login.microsoftonline.com') ||
      url.includes('microsoft.com/oauth2') ||
      url.includes('microsoftonline.com')
    );
  }

  private encodeRequestParams(request: HttpRequest<any>): HttpRequest<any> {
    if (!(request.params instanceof HttpParams) || request.params.keys().length === 0) {
      return request;
    }

    const originalUrl = new URL(request.url);
    const searchParams = new URLSearchParams();

    request.params.keys().forEach((key) => {
      request.params.getAll(key).forEach((value) => {
        searchParams.append(encodeURIComponent(key), encodeURIComponent(value));
      });
    });

    return request.clone({
      url: `${originalUrl.origin}${originalUrl.pathname}?${searchParams.toString()}`,
      params: new HttpParams(),
    });
  }

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private handleJwtAuth(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.jwtState?.token) {
      return next.handle(this.addAuthHeader(request, this.jwtState.token));
    }
    return next.handle(request);
  }

  private handleMsalAuth(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const needsToken = !this.msalState?.token || this.isTokenExpired(this.msalState?.expiry);

    if (needsToken) {
      return this.refreshMsalToken().pipe(
        switchMap((token: string) => {
          const authorizedRequest = this.addAuthHeader(request, token);
          return next.handle(authorizedRequest);
        }),
        catchError((error) => {
          console.error('Failed to refresh MSAL token:', error);
          if (this.msalState?.token) {
            const fallbackRequest = this.addAuthHeader(request, this.msalState.token);
            return next.handle(fallbackRequest);
          }
          return next.handle(request);
        }),
      );
    }

    return next.handle(this.addAuthHeader(request, this.msalState.token));
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = request.url.startsWith(environment.apiUrl);
    const isMsalUrl = this.isMsalAuthUrl(request.url);
    if (!isApiUrl || isMsalUrl) {
      return next.handle(request);
    }
    const modifiedRequest = this.encodeRequestParams(request);
    if (this.currentAuthMethod === 'jwt') {
      return this.handleJwtAuth(modifiedRequest, next);
    }
    if (this.currentAuthMethod === 'msal') {
      return this.handleMsalAuth(modifiedRequest, next);
    }
    return next.handle(modifiedRequest);
  }
}
